import ChecklistItem, {
  DocumentRequirement,
  InfoItem,
} from "models/checklist/checklist-item/checklist-item";
import useNavigateWithQuery from "./useNavigateWithQuery";
import { ChecklistItemType } from "models/checklist/checklist-item/checklist-item-type";
import { canUseSingleScannerInstance } from "services/document-capture/scanner-capabilities";
import { useAppStore } from "store/store";
import Logging from "logging";
import {
  AllowedDocumentType,
  AllowedDocumentsSet,
} from "models/documents/allowed-document";
import usePaxSession from "../usePaxSession";
import {
  getItemRelativeUrl,
  getPaxSessionRelativeUrl,
} from "./useFlowRedirect";
import {
  ItemLevelNavigationState,
  ItemNavigationState,
  ItemTypeNavigationState,
} from "./useItemLevelNavigationState";
import PaxSession from "models/session/pax-session";
import { PaxCheckDocumentSidesExpectation } from "api/models/PaxCheckDocumentSidesExpectation";
import ChecklistStatus from "models/checklist/checklist-status";

/*
takes in AllowedDocumentsSet with a set of allowed documents
and returns whether item resolver has to be used for it or not.

need to use item resolver when either:
- backend says so explicitly (shouldUseItemResolver),
- or when single scanner instance CAN'T be used to scan all allowed documents.
  for example, one doc in allowed set expects both sides to scan, whereas
  other doc(s) expects only one side

item resolver then requires user to pick exact document type for backend
to process with forceDocumentType search parameter
*/
function shouldEnforceItemResolver(
  allowedDocumentsSet: AllowedDocumentsSet
): boolean {
  return (
    // 1) either backend says to use item resolver (forceDocumentType required),
    // ie if common docs processing backend can be used for all allowed docs
    // — this will be deprecated, once backend no longer requires it
    allowedDocumentsSet.shouldUseItemResolver ||
    // 2) or scan is possible for all allowed docs,
    // but can't use single scanner instance to cover all of them
    (allowedDocumentsSet.isScanPossibleForAny &&
      !canUseSingleScannerInstance(allowedDocumentsSet.allowedDocuments)) ||
    // 3) or mixed single-side vs multi-side expectations
    // (eg one doc expects both sides, another expects only one)
    !haveSameSidesExpectation(allowedDocumentsSet)
  );
}

const isSingleSideExpectation = (
  sidesExpectation: PaxCheckDocumentSidesExpectation
): boolean =>
  sidesExpectation === PaxCheckDocumentSidesExpectation.DATA_PAGE ||
  sidesExpectation === PaxCheckDocumentSidesExpectation.CARD_FRONT ||
  sidesExpectation === PaxCheckDocumentSidesExpectation.CARD_BACK;

/*
checks whether all documents in an allowed documents set
can be captured with the same sides flow.

Single-side expectations (data page, card front, card back) are treated as
compatible — each doc only needs one side scanned, even if the exact side differs.
Only ALL (both sides) is incompatible with single-side docs.
*/
function haveSameSidesExpectation(
  allowedDocumentsSet: AllowedDocumentsSet
): boolean {
  const sidesExpectations = allowedDocumentsSet.allowedDocuments.map(
    (doc) => doc.sidesExpectation
  );

  const allSingleSide = sidesExpectations.every(isSingleSideExpectation);
  const allMultiSide = sidesExpectations.every(
    (sidesExpectation) =>
      sidesExpectation === PaxCheckDocumentSidesExpectation.ALL
  );

  return allSingleSide || allMultiSide;
}

/*
returns navigation URL for item "resolver" page to use:
- passport
  allowed document(s) - TRAVEL_DOC only

- item-resolver // TODO rename to /pick-document
  allowed documents can't be uploaded without specifying the exact processing
  doc type (forceDocType). For example, Indian visa item, which can be resolved
  either by Embassy visa (sticker in passport), or e-Visa (PDF) - which can't be
  processed by a single docs processing backend on backend, so user has to pick
  what kind of document exactly is being treated.
  Additionally, if a set of allowed documents can't be resolved using same methods,
  eg one document can be scanned and uploaded and other can only be uploaded.
  Lastly, item-resolver is enforced when a set of allowed documents can't be scanned
  using single scanner instance.

- basic-document (default)
  allowed document(s) can be resolved using same methods
  (eg only upload, or scan+upload, etc)
*/
function getItemResolverRelativeUrl(
  urlPrefix: string,
  allowedDocumentsSet: AllowedDocumentsSet
): string {
  const containsOnlyPassports = !allowedDocumentsSet.allowedDocuments.some(
    (doc) => doc.type !== AllowedDocumentType.Passport
  );

  if (containsOnlyPassports) {
    // passport
    // TODO Alex replace with generic travel document page
    return `${urlPrefix}/passport`;
  }

  if (shouldEnforceItemResolver(allowedDocumentsSet)) {
    // item resolver
    return `${urlPrefix}/item-resolver`;
  }

  // "basic" document
  return `${urlPrefix}/document`;
}

/*
function returns a relative URL for item, ie
/session/{sessionId}/pax/{paxSessionId}/item/{itemId}/passport
*/
export function getRedirectURLForItem(
  sessionId: string,
  paxSessionId: string,
  item: ChecklistItem
): string {
  const itemRelativeUrl = getItemRelativeUrl(sessionId, paxSessionId, item.id);

  if (item instanceof InfoItem) {
    // info item
    return `${itemRelativeUrl}/info`;
  }

  const docReqItem = item as DocumentRequirement;

  const isManualOnly =
    docReqItem.allowedDocumentsSet.allowedDocuments.length > 0 &&
    docReqItem.allowedDocumentsSet.allowedDocuments.every(
      (doc) =>
        doc.isManualPossible() && !doc.isScanPossible() && !doc.isUploadPossible()
    );

  // Manual input is currently only implemented for ONWARD_TICKET (see ManualInputPage).
  // Other manual-only requirements fall through to the regular item pages to avoid
  // landing on a blank manual-input page.
  if (isManualOnly && docReqItem.itemType === ChecklistItemType.OnwardTicket) {
    return `${itemRelativeUrl}/manual-input`;
  }

  if (docReqItem.allowedDocumentsSet.allowedDocuments.length > 0) {
    return getItemResolverRelativeUrl(
      `${itemRelativeUrl}`,
      docReqItem.allowedDocumentsSet
    );
  }

  const paxSessionRelativeUrl = getPaxSessionRelativeUrl(
    sessionId,
    paxSessionId
  );

  // something is wrong - no allowed documents
  Logging.log(
    "item-details",
    paxSessionId,
    `No allowed documents for item id ${item.id} of type ${docReqItem.itemType}`
  );
  return `${paxSessionRelativeUrl}/checklist`;
}

/*
function returns a relative URL for itemType, ie
/session/{sessionId}/pax/{paxSessionId}/item-type/TRAVEL_DOC/passport
*/
export function getRedirectURLForItemType(
  sessionId: string,
  paxSession: PaxSession,
  itemType: string
): string {
  const paxSessionRelativeUrl = getPaxSessionRelativeUrl(
    sessionId,
    paxSession.paxSessionId
  );

  /*
   * item type navigation, currently supported only for TRAVEL_DOC
   */
  if (itemType === ChecklistItemType.TravelDoc) {
    // itemId is not present, but itemType is and it is TRAVEL_DOC
    const itemTypeRelativeUrl = `${paxSessionRelativeUrl}/item-type`;

    const docsSetFromChecklist = paxSession.checklist.allowedTravelDocumentsSet;
    if (docsSetFromChecklist.allowedDocuments.length > 0) {
      return getItemResolverRelativeUrl(
        `${itemTypeRelativeUrl}/${ChecklistItemType.TravelDoc}`,
        docsSetFromChecklist
      );
    }

    if (paxSession.checklist.status !== ChecklistStatus.OnHold) {
      // something is wrong - no allowed documents
      Logging.log(
        "item-details",
        paxSession.paxSessionId,
        `No allowed documents for type TRAVEL_DOC`
      );
    }

    return `${paxSessionRelativeUrl}/checklist`;
  }

  // not travel doc
  Logging.log(
    "item-details",
    paxSession.paxSessionId,
    `Only TRAVEL_DOC itemType is currently supported (provided itemType: ${itemType})`
  );
  return `${paxSessionRelativeUrl}/checklist`;
}

/*
function returns a relative URL for either item or itemType,
depending on ItemLevelNavigationState, ie:
/session/{sessionId}/pax/{paxSessionId}/item-type/TRAVEL_DOC/document
OR
/session/{sessionId}/pax/{paxSessionId}/item/{itemId}/item-resolver
*/
export function getItemLevelRedirectURL(
  sessionId: string,
  paxSession: PaxSession,
  itemLevelNavigationState: ItemLevelNavigationState
): string {
  // itemId
  if (itemLevelNavigationState instanceof ItemNavigationState) {
    return getRedirectURLForItem(
      sessionId,
      paxSession.paxSessionId,
      itemLevelNavigationState.item
    );
  }

  // itemType
  if (itemLevelNavigationState instanceof ItemTypeNavigationState) {
    return getRedirectURLForItemType(
      sessionId,
      paxSession,
      itemLevelNavigationState.itemType
    );
  }

  const paxSessionRelativeUrl = getPaxSessionRelativeUrl(
    sessionId,
    paxSession.paxSessionId
  );

  // neither - should not be the case
  // push to checklist default navigation to handle
  Logging.log(
    "item-details",
    paxSession.paxSessionId,
    `Neither item id nor itemType were given`
  );
  return `${paxSessionRelativeUrl}/checklist`;
}

/*
Hook to navigate to item/item type page given itemType or item.
It preserves forceDocumentType from search.
*/
const useItemLevelRedirect = (
  itemLevelNavigationState: ItemLevelNavigationState
) => {
  const navigate = useNavigateWithQuery();

  const sessionId = useAppStore((state) => state.sessionId);
  const paxSession = usePaxSession();

  if (!paxSession) {
    throw new Error("Invalid paxSession");
  }

  const navigateToItemPage = () =>
    navigate(
      // keep search as it is (preserve forceDocumentType, if present)
      getItemLevelRedirectURL(sessionId, paxSession, itemLevelNavigationState)
    );

  return navigateToItemPage;
};

export default useItemLevelRedirect;
